import React, { useState, useEffect } from "react";
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, where } from "firebase/firestore";
import { db } from "@/src/lib/firebase";
import { useAuth } from "@/src/lib/AuthContext";
import { Announcement, AnnouncementCategory, MallZone } from "@/src/types";
import { ANNOUNCEMENT_CATEGORIES, MALL_ZONES } from "@/src/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Search, MapPin, Tag, Phone, Mail, Megaphone, Plus, Briefcase, Home, Key, ShoppingBag, Wrench, MoreHorizontal, Loader2, Calendar } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

const CATEGORY_ICONS: Record<string, any> = {
  empleo: Briefcase,
  vivienda: Home,
  alquiler: Key,
  articulos: ShoppingBag,
  servicios: Wrench,
  otros: MoreHorizontal,
};

export default function AnnouncementsPage() {
  const { user, profile } = useAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedZone, setSelectedZone] = useState<string>("all");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "otros" as AnnouncementCategory,
    mallZone: "otros" as MallZone,
    price: "",
    contactPhone: "",
    contactEmail: ""
  });

  useEffect(() => {
    let q = query(collection(db, "announcements"), orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      })) as Announcement[];
      setAnnouncements(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handlePost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast.error("Debes iniciar sesión para publicar");
      return;
    }

    setIsSubmitting(true);
    try {
      const announcementData = {
        ...formData,
        authorUid: user.uid,
        authorName: profile?.displayName || "Anónimo",
        price: formData.price ? parseFloat(formData.price) : undefined,
        createdAt: new Date().toISOString(),
      };

      await addDoc(collection(db, "announcements"), announcementData);
      toast.success("¡Anuncio publicado correctamente!");
      setIsCreateOpen(false);
      setFormData({
        title: "",
        description: "",
        category: "otros",
        mallZone: "otros",
        price: "",
        contactPhone: "",
        contactEmail: ""
      });
    } catch (error) {
      console.error(error);
      toast.error("Error al publicar el anuncio");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredAnnouncements = announcements.filter(a => {
    const matchesSearch = a.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         a.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || a.category === selectedCategory;
    const matchesZone = selectedZone === "all" || a.mallZone === selectedZone;
    return matchesSearch && matchesCategory && matchesZone;
  });

  const getCategoryLabel = (cat: string) => ANNOUNCEMENT_CATEGORIES.find(c => c.id === cat)?.label || cat;
  const getZoneLabel = (zone: string) => MALL_ZONES.find(z => z.id === zone)?.label || zone;

  return (
    <div className="container px-4 md:px-8 py-8 min-h-screen">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight mb-2">Tablón de Anuncios</h1>
          <p className="text-muted-foreground">Encuentra empleo, vivienda y más en el Mall Virtual.</p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-primary hover:bg-primary/90">
              <Plus className="h-4 w-4" /> Publicar Anuncio
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Publicar un Anuncio</DialogTitle>
              <DialogDescription>
                Completa los datos para que otros usuarios puedan verlo en el tablón.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handlePost} className="space-y-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="title">Título del Anuncio</Label>
                <Input 
                  id="title" 
                  placeholder="Ej: Se busca vendedor de medio tiempo" 
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Categoría</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(val: AnnouncementCategory) => setFormData({...formData, category: val})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {ANNOUNCEMENT_CATEGORIES.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Zona del Mall</Label>
                  <Select 
                    value={formData.mallZone} 
                    onValueChange={(val: MallZone) => setFormData({...formData, mallZone: val})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Zona" />
                    </SelectTrigger>
                    <SelectContent>
                      {MALL_ZONES.map(zone => (
                        <SelectItem key={zone.id} value={zone.id}>{zone.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="description">Descripción</Label>
                <Textarea 
                  id="description" 
                  placeholder="Detalles sobre el anuncio..." 
                  className="min-h-[100px]"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="price">Precio / Salario (Opcional)</Label>
                <Input 
                  id="price" 
                  type="number" 
                  placeholder="0.00" 
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="phone">Teléfono de contacto</Label>
                  <Input 
                    id="phone" 
                    placeholder="WatsApp" 
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({...formData, contactPhone: e.target.value})}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    placeholder="email@ejemplo.com" 
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({...formData, contactEmail: e.target.value})}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : "Publicar Ahora"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-6 md:grid-cols-4">
        {/* Filters Sidebar */}
        <div className="md:col-span-1 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Filtros</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Buscar</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Buscar..." 
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Categoría</Label>
                <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full">
                  <TabsList className="grid grid-cols-2 h-auto gap-1 bg-transparent p-0">
                    <TabsTrigger value="all" className="data-[state=active]:bg-primary data-[state=active]:text-white">Todo</TabsTrigger>
                    {ANNOUNCEMENT_CATEGORIES.map(cat => (
                      <TabsTrigger 
                        key={cat.id} 
                        value={cat.id}
                        className="data-[state=active]:bg-primary data-[state=active]:text-white text-xs py-1"
                      >
                        {cat.label.split(" ").pop()}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>

              <div className="space-y-2">
                <Label>Zona del Mall</Label>
                <Select value={selectedZone} onValueChange={setSelectedZone}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todas las zonas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las zonas</SelectItem>
                    {MALL_ZONES.map(zone => (
                      <SelectItem key={zone.id} value={zone.id}>{zone.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4 flex gap-3 items-center">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Megaphone className="h-5 w-5 text-primary" />
              </div>
              <div className="text-xs">
                <p className="font-bold">¿Necesitas algo?</p>
                <p className="text-muted-foreground">Publica tu aviso y llega a miles de personas en el Mall Virtual.</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="md:col-span-3">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Cargando anuncios...</p>
            </div>
          ) : filteredAnnouncements.length === 0 ? (
            <div className="text-center py-24 border-2 border-dashed rounded-2xl bg-muted/20">
              <Megaphone className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No se encontraron anuncios</h3>
              <p className="text-muted-foreground mb-6">Prueba con otros filtros o sé el primero en publicar un anuncio.</p>
              <Button onClick={() => setIsCreateOpen(true)}>Publicar Primer Anuncio</Button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <AnimatePresence mode="popLayout">
                {filteredAnnouncements.map((announcement) => {
                  const Icon = CATEGORY_ICONS[announcement.category] || MoreHorizontal;
                  return (
                    <motion.div
                      key={announcement.id}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Card className="h-full flex flex-col hover:border-primary/50 transition-colors cursor-default overflow-hidden">
                        <div className="h-1 bg-primary/10" />
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-start gap-2 mb-2">
                            <Badge variant="outline" className="flex items-center gap-1">
                              <Icon className="h-3 w-3" />
                              {getCategoryLabel(announcement.category)}
                            </Badge>
                            {announcement.price && (
                              <span className="font-bold text-primary">${announcement.price.toLocaleString()}</span>
                            )}
                          </div>
                          <CardTitle className="leading-tight text-xl">{announcement.title}</CardTitle>
                        </CardHeader>
                        <CardContent className="flex-1 space-y-4">
                          <p className="text-sm text-muted-foreground line-clamp-3">
                            {announcement.description}
                          </p>
                          <div className="space-y-2 border-t pt-3">
                            <div className="flex items-center text-xs text-muted-foreground">
                              <MapPin className="h-3 w-3 mr-1.5" />
                              {getZoneLabel(announcement.mallZone)}
                            </div>
                            <div className="flex items-center text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3 mr-1.5" />
                              Publicado: {new Date(announcement.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                        </CardContent>
                        <CardFooter className="bg-muted/30 pt-4 flex flex-col gap-2">
                          <div className="flex items-center justify-between w-full">
                            <span className="text-[10px] text-muted-foreground uppercase font-semibold">Contacto</span>
                            <span className="text-xs font-medium">{announcement.authorName}</span>
                          </div>
                          <div className="flex gap-2 w-full">
                            {announcement.contactPhone && (
                              <Button asChild variant="outline" size="sm" className="flex-1 text-[10px] h-8 bg-white">
                                <a href={`tel:${announcement.contactPhone}`}>
                                  <Phone className="h-3 w-3 mr-1.5" /> Llamar
                                </a>
                              </Button>
                            )}
                            {announcement.contactEmail && (
                              <Button asChild variant="outline" size="sm" className="flex-1 text-[10px] h-8 bg-white">
                                <a href={`mailto:${announcement.contactEmail}`}>
                                  <Mail className="h-3 w-3 mr-1.5" /> Email
                                </a>
                              </Button>
                            )}
                          </div>
                        </CardFooter>
                      </Card>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
